import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CHANNEL_STATUS, IChannel, PROTOCOL } from '@/entities/channel';
import { CreateTgBotDialog, MaxAuthDialog, useDeleteBotToken } from '@/features/channels';
import { TranslationFunctionType } from '@/shared/globalTypes';
import { useGetConfirmation } from '@/shared/ui/Confirmations/confirmation';
import { Button } from '@/shared/ui/lib/Button';
import { useNotifications } from '@/shared/ui/Notifications';
import { CustomTypography } from '@/shared/ui/Typography';
import { getRoutePath } from '@/shared/utils/routes';

import { SettingBlockItem } from './SettingBlockItem';

type TBotSettingsBlock = {
  channel: IChannel;
};

const renderConfirmBody = (t: TranslationFunctionType) => {
  return (
    <>
      <CustomTypography>
        {t('channels:settings.changeTgBotDialog.confirmChangingToken.part1')}
      </CustomTypography>
      <CustomTypography>
        {t('channels:settings.changeTgBotDialog.confirmChangingToken.part2')}
      </CustomTypography>
    </>
  );
};

export const ChangeBotBlock = ({ channel }: TBotSettingsBlock) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { getConfirmation } = useGetConfirmation();
  const { addNotification } = useNotifications();
  const { deleteBotToken, isPending: isDeletingToken } = useDeleteBotToken();
  const [isCreateTgBotDialogOpen, setCreateTgBotDialogOpen] = useState(false);

  const isUnauthorizedStatus = [
    CHANNEL_STATUS.UNAUTHORIZED,
    CHANNEL_STATUS.QR_IN_PROGRESS,
    CHANNEL_STATUS.PASSWORD_REQUIRED,
  ].some((status) => status === channel.status);

  const handleCloseCreateDialog = () => {
    setCreateTgBotDialogOpen(false);
  };

  const handleTokenCreated = () => {
    navigate(getRoutePath('MAIN', 'CHANNELS'));
    setCreateTgBotDialogOpen(false);
    addNotification({
      type: 'success',
      title: t('channels:settings.changeTgBotDialog.successChanged'),
    });
  };

  const handleOpenTokenChange = async () => {
    const isConfirmed = await getConfirmation({
      title: t('channels:settings.changeTgBotDialog.changeTgBot'),
      description: renderConfirmBody(t),
      confirmText: t('channels:settings.changeTgBotDialog.confirm'),
      shouldCloseOnConfirm: true,
    });

    if (isConfirmed) {
      setCreateTgBotDialogOpen(true);
    }
  };

  const handleDeleteToken = async () => {
    const isConfirmed = await getConfirmation({
      title: t('channels:settings.deleteTokenBotDialog.title'),
      description: t('channels:settings.deleteTokenBotDialog.description'),
      confirmText: t('channels:settings.deleteTokenBotDialog.confirm'),
      shouldCloseOnConfirm: true,
      priorityButton: 'confirm',
    });

    if (isConfirmed) {
      await deleteBotToken(channel.id, () => {
        addNotification({
          type: 'success',
          title: t('channels:settings.deleteTokenBotDialog.successDeleted'),
        });
      });
    }
  };

  return (
    <SettingBlockItem title={t('channels:settings.bot')}>
      <CustomTypography color="text.secondary">{channel.name}</CustomTypography>
      {!isUnauthorizedStatus ? (
        <>
          <Button variant="text" onClick={handleOpenTokenChange}>
            {t('channels:settings.changeTokenBot')}
          </Button>
          <Button
            variant="text"
            color="error"
            loading={isDeletingToken}
            onClick={handleDeleteToken}
          >
            {t('channels:settings.deleteTokenBot')}
          </Button>
        </>
      ) : null}
      {channel.protocol === PROTOCOL.MAX ? (
        <MaxAuthDialog
          open={isCreateTgBotDialogOpen}
          onClose={handleCloseCreateDialog}
          onCreated={handleTokenCreated}
          channelId={channel.id}
          modeDialog="change"
        />
      ) : (
        <CreateTgBotDialog
          open={isCreateTgBotDialogOpen}
          onClose={handleCloseCreateDialog}
          onCreated={handleTokenCreated}
          channelId={channel.id}
          modeDialog="change"
        />
      )}
    </SettingBlockItem>
  );
};
